import { useState } from 'react';
import { toFa } from '../../utils/faFormat';
import WaitlistForm from './WaitlistForm';

const DEFAULT_SORT = { key: 'perMonth', direction: 'asc' };

function finiteNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function positiveNumber(value) {
  const number = finiteNumber(value);
  return number !== null && number > 0 ? number : null;
}

function perMonth(plan) {
  const amount = positiveNumber(plan.amount_irt);
  const months = positiveNumber(plan.contract_months);
  return amount !== null && months !== null ? amount / months : null;
}

function perGigabyte(plan) {
  const amount = positiveNumber(plan.amount_irt);
  const volume = positiveNumber(plan.included_volume_gb);
  return amount !== null && volume !== null ? amount / volume : null;
}

function formatNumber(value) {
  const number = finiteNumber(value);
  if (number === null) return '';
  const rounded = Math.round(number);
  const grouped = String(rounded).replace(/\B(?=(\d{3})+(?!\d))/g, '٬');
  return toFa(grouped);
}

function displayText(value) {
  return toFa(value == null ? '' : String(value));
}

function formatMetric(value, unit) {
  return finiteNumber(value) === null ? 'اعلام نشده' : `${formatNumber(value)} ${unit}`;
}

function formatTerm(plan) {
  const months = positiveNumber(plan.contract_months);
  if (months !== null) return `${formatNumber(months)} ماه`;
  const days = positiveNumber(plan.included_volume_days);
  if (days !== null) return `${formatNumber(days)} روز`;
  return 'بدون دورهٔ قراردادی';
}

function formatPrice(amount) {
  return finiteNumber(amount) === null
    ? 'قیمت اعلام نشده'
    : `${formatNumber(amount)} تومان`;
}

function carrierIsServiceable(carrier) {
  return Array.isArray(carrier.services)
    && carrier.services.some((service) => service.decision === 'serviceable');
}

function regionLabels(carriers, decision) {
  const labels = carriers.flatMap((carrier) => (
    Array.isArray(carrier.services)
      ? carrier.services
        .filter((service) => !decision || service.decision === decision)
        .map((service) => service.region?.label)
      : []
  )).filter(Boolean);
  return [...new Set(labels)].map(displayText);
}

function carrierChipStyle(carrier) {
  return {
    backgroundColor: carrier.map_color || 'var(--c-primary)',
    color: carrier.map_foreground_color || 'var(--c-surface)',
  };
}

function sortValue(plan, key) {
  if (key === 'price') return finiteNumber(plan.amount_irt);
  if (key === 'volume') return finiteNumber(plan.included_volume_gb);
  if (key === 'perGigabyte') return perGigabyte(plan);
  return perMonth(plan);
}

function sortedPlans(plans, sort) {
  return plans
    .map((plan, index) => ({ plan, index }))
    .sort((a, b) => {
      const aValue = sortValue(a.plan, sort.key);
      const bValue = sortValue(b.plan, sort.key);
      if (aValue === null && bValue === null) return a.index - b.index;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      const difference = aValue - bValue;
      if (difference === 0) return a.index - b.index;
      return sort.direction === 'asc' ? difference : -difference;
    })
    .map(({ plan }) => plan);
}

function SortableHeader({ columnKey, label, sort, onSort }) {
  const active = sort.key === columnKey;
  const ariaSort = active
    ? (sort.direction === 'asc' ? 'ascending' : 'descending')
    : 'none';

  return (
    <th scope="col" aria-sort={ariaSort}>
      <button
        type="button"
        className={`coverage-sort${active ? ' is-active' : ''}`}
        onClick={() => onSort(columnKey)}
        aria-label={`مرتب‌سازی بر پایهٔ ${label}`}
      >
        <span>{label}</span>
        {active && (
          <span className="coverage-sort-indicator" aria-hidden="true">
            {sort.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </button>
    </th>
  );
}

function DerivedCell({ value, minimum }) {
  if (value === null) return <td className="coverage-derived-cell" />;
  const cheapest = minimum !== null && value === minimum;
  return (
    <td className={`coverage-derived-cell${cheapest ? ' is-cheapest' : ''}`}>
      <span>{formatNumber(value)}</span>
      {cheapest && <span className="coverage-cheapest-marker">کمترین</span>}
    </td>
  );
}

function CarrierOffers({ carrier, sort, onSort, minimums, onRegister, busy }) {
  const plans = Array.isArray(carrier.plans) ? carrier.plans : [];
  const carrierName = displayText(carrier.display_name);
  const orderedPlans = sortedPlans(plans, sort);

  return (
    <section className="coverage-carrier" aria-label={`بسته‌های اپراتور ${carrierName}`}>
      <div className="coverage-carrier-header">
        <h3>
          <span className="coverage-carrier-chip" style={carrierChipStyle(carrier)}>
            {carrierName}
          </span>
        </h3>
        <button
          type="button"
          className="coverage-primary-action"
          onClick={() => onRegister(carrier)}
          disabled={busy}
          aria-label={`ثبت‌نام با اپراتور ${carrierName}`}
        >
          ثبت‌نام
        </button>
      </div>

      {plans.length === 0 ? (
        <p className="coverage-carrier-empty">هنوز بسته‌ای برای این اپراتور منتشر نشده است.</p>
      ) : (
        <div className="coverage-table-scroll">
          <table className="coverage-table" aria-label={`مقایسهٔ بسته‌های ${carrierName}`}>
            <thead>
              <tr>
                <th scope="col">نام طرح</th>
                <th scope="col">سرعت</th>
                <SortableHeader columnKey="volume" label="حجم" sort={sort} onSort={onSort} />
                <th scope="col">مدت</th>
                <SortableHeader columnKey="price" label="قیمت" sort={sort} onSort={onSort} />
                <SortableHeader columnKey="perMonth" label="تومان در ماه" sort={sort} onSort={onSort} />
                <SortableHeader columnKey="perGigabyte" label="تومان به ازای گیگ" sort={sort} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {orderedPlans.map((plan, index) => {
                const monthly = perMonth(plan);
                const perGb = perGigabyte(plan);
                return (
                  <tr key={plan.plan_id || `${carrier.carrier_id}-${index}`}>
                    <td className="coverage-plan-name">{displayText(plan.display_name)}</td>
                    <td>{formatMetric(plan.speed_down_mbps, 'مگابیت')}</td>
                    <td>{formatMetric(plan.included_volume_gb, 'گیگابایت')}</td>
                    <td>{formatTerm(plan)}</td>
                    <td className="coverage-price">{formatPrice(plan.amount_irt)}</td>
                    <DerivedCell value={monthly} minimum={minimums.perMonth} />
                    <DerivedCell value={perGb} minimum={minimums.perGigabyte} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ManualCheck({ carriers, onAsk, busy, compact = false }) {
  const regions = regionLabels(carriers, 'unknown');
  return (
    <section className={`coverage-manual${compact ? ' is-compact' : ''}`} aria-label="نیاز به بررسی دستی پوشش">
      <h2>نیاز به بررسی دستی</h2>
      <p>
        وضعیت این نقطه هنوز قطعی نیست و نباید آن را تحت پوشش یا خارج از پوشش در نظر گرفت.
      </p>
      {regions.length > 0 && (
        <p className="coverage-region">محدودهٔ بررسی: <strong>{regions.join('، ')}</strong></p>
      )}
      <div className="coverage-manual-list">
        {carriers.map((carrier, index) => {
          const carrierName = displayText(carrier.display_name);
          return (
            <div className="coverage-manual-carrier" key={carrier.carrier_id || index}>
              <span className="coverage-carrier-chip" style={carrierChipStyle(carrier)}>
                {carrierName}
              </span>
              <button
                type="button"
                className="coverage-secondary-action"
                onClick={() => onAsk(carrier)}
                disabled={busy}
                aria-label={`درخواست بررسی دستی ${carrierName} در گفتگو`}
              >
                بررسی با اپراتور در گفتگو
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function CoveragePanel({ consult }) {
  const [sort, setSort] = useState(DEFAULT_SORT);
  const {
    location,
    coverageLoading,
    coverage,
    coverageError,
    recheckCoverage,
    sendMessage,
    busy,
  } = consult;

  const handleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // The agent is the authority for the funnel. This panel is only a visual
  // surface that sends the customer's intent back to the agent.
  const registerWithCarrier = (carrier) => {
    const carrierName = displayText(carrier.display_name);
    sendMessage(`برای ثبت‌نام فیبر نوری در این نقطه با اپراتور «${carrierName}» اقدام کن.`);
  };

  const joinWaitlist = (phone) => {
    sendMessage(`برای پیش‌ثبت‌نام فیبر نوری در این نقطه، شماره همراه من ${toFa(phone)} است. لطفاً آن را ثبت کن.`);
  };

  const askForManualCheck = (carrier) => {
    const carrierName = displayText(carrier.display_name);
    sendMessage(`لطفاً پوشش اپراتور «${carrierName}» را برای این نقطه به‌صورت دستی بررسی کن.`);
  };

  if (!location) {
    return (
      <aside className="coverage-panel is-quiet" aria-label="وضعیت پوشش و مقایسهٔ بسته‌ها">
        <p>برای دیدن پوشش و مقایسهٔ بسته‌ها، یک نقطه روی نقشه انتخاب کنید.</p>
      </aside>
    );
  }

  if (coverageLoading || (!coverage && !coverageError)) {
    return (
      <aside className="coverage-panel" aria-label="وضعیت پوشش و مقایسهٔ بسته‌ها">
        <div className="coverage-loading" role="status" aria-live="polite">
          <span className="coverage-spinner" aria-hidden="true" />
          <span>در حال بررسی پوشش…</span>
        </div>
      </aside>
    );
  }

  if (coverageError) {
    return (
      <aside className="coverage-panel" aria-label="وضعیت پوشش و مقایسهٔ بسته‌ها">
        <div className="coverage-error" role="alert">
          <div>
            <h2>بررسی پوشش انجام نشد</h2>
            <p>لطفاً دوباره تلاش کنید.</p>
          </div>
          <button type="button" className="coverage-secondary-action" onClick={recheckCoverage}>
            تلاش دوباره
          </button>
        </div>
      </aside>
    );
  }

  const carriers = Array.isArray(coverage?.carriers) ? coverage.carriers : [];
  const serviceableCarriers = carriers.filter(carrierIsServiceable);
  const unknownCarriers = carriers.filter((carrier) => !carrierIsServiceable(carrier));

  if (serviceableCarriers.length === 0 && unknownCarriers.length > 0) {
    return (
      <aside className="coverage-panel" aria-label="وضعیت پوشش و مقایسهٔ بسته‌ها">
        <ManualCheck carriers={unknownCarriers} onAsk={askForManualCheck} busy={busy} />
      </aside>
    );
  }

  if (serviceableCarriers.length === 0) {
    return (
      <aside className="coverage-panel" aria-label="وضعیت پوشش و مقایسهٔ بسته‌ها">
        <section className="coverage-unavailable">
          <h2>این نقطه در حال حاضر تحت پوشش هیچ اپراتوری نیست.</h2>
          <p>اگر مایل باشید، شماره همراهتان را برای پیش‌ثبت‌نام وارد کنید.</p>
          <WaitlistForm onSubmit={joinWaitlist} disabled={busy} />
        </section>
      </aside>
    );
  }

  const regions = regionLabels(serviceableCarriers, 'serviceable');
  const allPlans = serviceableCarriers.flatMap((carrier) => (
    Array.isArray(carrier.plans) ? carrier.plans : []
  ));
  const monthlyValues = allPlans.map(perMonth).filter((value) => value !== null);
  const perGbValues = allPlans.map(perGigabyte).filter((value) => value !== null);
  const minimums = {
    perMonth: monthlyValues.length > 0 ? Math.min(...monthlyValues) : null,
    perGigabyte: perGbValues.length > 0 ? Math.min(...perGbValues) : null,
  };
  const pricingNote = coverage.pricing_note
    || serviceableCarriers.map((carrier) => carrier.pricing_note).find(Boolean);

  return (
    <aside className="coverage-panel" aria-label="وضعیت پوشش و مقایسهٔ بسته‌ها">
      <div className="coverage-panel-header">
        <h2>
          پوشش این نقطه برای {toFa(serviceableCarriers.length)} اپراتور تأیید شد
        </h2>
        {regions.length > 0 && (
          <p className="coverage-region">محدودهٔ پوشش: <strong>{regions.join('، ')}</strong></p>
        )}
      </div>

      {pricingNote && <p className="coverage-pricing-note">{displayText(pricingNote)}</p>}

      <div className="coverage-carriers">
        {serviceableCarriers.map((carrier, index) => (
          <CarrierOffers
            key={carrier.carrier_id || index}
            carrier={carrier}
            sort={sort}
            onSort={handleSort}
            minimums={minimums}
            onRegister={registerWithCarrier}
            busy={busy}
          />
        ))}
      </div>

      {unknownCarriers.length > 0 && (
        <ManualCheck
          carriers={unknownCarriers}
          onAsk={askForManualCheck}
          busy={busy}
          compact
        />
      )}
    </aside>
  );
}
